import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const COLORS = ['var(--color-primary)', 'var(--color-secondary)', 'var(--color-success)', 'var(--color-warning)', 'var(--color-error)'];

const AnalyticsReports = () => {
  const [userGrowth, setUserGrowth] = useState([]);
  const [campaignPerf, setCampaignPerf] = useState([]);
  const [financialData, setFinancialData] = useState({ totalPaid: 0, rewardLiability: 0, totalBudgets: 0 });
  const [influencerDist, setInfluencerDist] = useState([]);
  const [rewardROI, setRewardROI] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, txRes, campaignsRes, ledgerRes, scoresRes] = await Promise.all([
        supabase?.from('user_profiles')?.select('created_at, role'),
        supabase?.from('transactions')?.select('created_at, status, amount, campaign_id'),
        supabase?.from('campaigns')?.select('id, title, budget, spent, reward_amount'),
        supabase?.from('wallet_ledger')?.select('amount, ledger_type'),
        supabase?.from('scores')?.select('total_score'),
      ]);

      // User growth by month
      const monthMap = {};
      usersRes?.data?.forEach(u => {
        const d = new Date(u?.created_at);
        const key = d?.toLocaleString('default', { month: 'short', year: '2-digit' });
        if (!monthMap?.[key]) monthMap[key] = { name: key, users: 0, influencers: 0, brands: 0 };
        monthMap[key].users += 1;
        if (u?.role === 'influencer') monthMap[key].influencers += 1;
        if (u?.role === 'brand') monthMap[key].brands += 1;
      });
      setUserGrowth(Object.values(monthMap)?.slice(-8));

      // Influencer distribution by score tier
      const scores = scoresRes?.data?.map(s => s?.total_score) || [];
      const tiers = { 'Bronze (0-25)': 0, 'Silver (26-50)': 0, 'Gold (51-75)': 0, 'Platinum (76+)': 0 };
      scores?.forEach(s => {
        if (s <= 25) tiers['Bronze (0-25)'] += 1;
        else if (s <= 50) tiers['Silver (26-50)'] += 1;
        else if (s <= 75) tiers['Gold (51-75)'] += 1;
        else tiers['Platinum (76+)'] += 1;
      });
      setInfluencerDist(Object.entries(tiers)?.map(([name, value]) => ({ name, value })));

      // Campaign performance
      const campMap = {};
      txRes?.data?.forEach(t => {
        if (!t?.campaign_id) return;
        if (!campMap?.[t?.campaign_id]) campMap[t?.campaign_id] = { submissions: 0, approved: 0, rewards: 0 };
        campMap[t?.campaign_id].submissions += 1;
        if (t?.status === 'approved') { campMap[t?.campaign_id].approved += 1; campMap[t?.campaign_id].rewards += parseFloat(t?.amount || 0); }
      });
      const campPerf = campaignsRes?.data?.map(c => ({
        name: c?.title?.length > 15 ? c?.title?.substring(0, 15) + '...' : c?.title,
        budget: parseFloat(c?.budget || 0),
        spent: parseFloat(c?.spent || 0),
        submissions: campMap?.[c?.id]?.submissions || 0,
        approved: campMap?.[c?.id]?.approved || 0,
      }))?.slice(0, 6);
      setCampaignPerf(campPerf);

      // Reward ROI
      const roiData = campaignsRes?.data?.map(c => {
        const spent = parseFloat(c?.spent || 0);
        const budget = parseFloat(c?.budget || 0);
        const roi = budget > 0 ? Math.round((spent / budget) * 100) : 0;
        return { name: c?.title?.length > 12 ? c?.title?.substring(0, 12) + '...' : c?.title, roi, spent };
      })?.slice(0, 6);
      setRewardROI(roiData);

      // Financial summary
      const credits = ledgerRes?.data?.filter(l => l?.ledger_type === 'credit')?.reduce((s, l) => s + parseFloat(l?.amount || 0), 0) || 0;
      const debits = ledgerRes?.data?.filter(l => l?.ledger_type === 'debit')?.reduce((s, l) => s + parseFloat(l?.amount || 0), 0) || 0;
      const totalBudgets = campaignsRes?.data?.reduce((s, c) => s + parseFloat(c?.budget || 0), 0) || 0;
      setFinancialData({ totalPaid: credits, rewardLiability: credits - debits, totalBudgets });
    } catch (err) {
      console.error('[AnalyticsReports] loadAnalytics error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);

  const handleExportReport = () => {
    const report = `SocialCents Analytics Report\nGenerated: ${new Date()?.toLocaleString()}\n\nFinancial Summary\nTotal Rewards Paid: $${financialData?.totalPaid?.toFixed(2)}\nReward Liability: $${financialData?.rewardLiability?.toFixed(2)}\nTotal Campaign Budgets: $${financialData?.totalBudgets?.toFixed(2)}\n`;
    const blob = new Blob([report], { type: 'text/plain;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `analytics_report_${new Date()?.toISOString()?.split('T')?.[0]}.txt`;
    document.body?.appendChild(a); a?.click(); document.body?.removeChild(a); URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Financial summary */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Financial Overview</h3>
        <Button variant="outline" size="sm" iconName="Download" iconPosition="left" onClick={handleExportReport}>Export Report</Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: 'Total Rewards Paid', value: `$${financialData?.totalPaid?.toFixed(2)}`, icon: 'DollarSign', color: 'text-success', bg: 'bg-success/10' },
          { label: 'Reward Liability', value: `$${financialData?.rewardLiability?.toFixed(2)}`, icon: 'AlertCircle', color: 'text-warning', bg: 'bg-warning/10' },
          { label: 'Total Campaign Budgets', value: `$${financialData?.totalBudgets?.toFixed(2)}`, icon: 'Briefcase', color: 'text-primary', bg: 'bg-primary/10' },
        ]?.map((s, i) => (
          <div key={i} className="bg-card rounded-xl border border-border p-4 flex items-center gap-4">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${s?.bg}`}>
              <Icon name={s?.icon} size={20} className={s?.color} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{s?.label}</p>
              <p className={`text-xl font-bold ${s?.color}`}>{s?.value}</p>
            </div>
          </div>
        ))}
      </div>
      {/* User growth chart */}
      <div className="bg-card rounded-xl border border-border p-5">
        <h3 className="text-sm font-semibold text-foreground mb-4">User Growth</h3>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={userGrowth}>
              <defs>
                <linearGradient id="userGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="name" stroke="var(--color-muted-foreground)" style={{ fontSize: '11px' }} />
              <YAxis stroke="var(--color-muted-foreground)" style={{ fontSize: '11px' }} />
              <Tooltip contentStyle={{ backgroundColor: 'var(--color-popover)', border: '1px solid var(--color-border)', borderRadius: '8px', color: 'var(--color-foreground)', fontSize: '12px' }} />
              <Legend wrapperStyle={{ fontSize: '11px' }} />
              <Area type="monotone" dataKey="users" name="Total Users" stroke="var(--color-primary)" fill="url(#userGrad)" strokeWidth={2} />
              <Area type="monotone" dataKey="influencers" name="Influencers" stroke="var(--color-secondary)" fill="none" strokeWidth={1.5} strokeDasharray="4 2" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Influencer distribution + Campaign performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-xl border border-border p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4">Influencer Score Distribution</h3>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={influencerDist} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                  {influencerDist?.map((_, i) => <Cell key={i} fill={COLORS?.[i % COLORS?.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: 'var(--color-popover)', border: '1px solid var(--color-border)', borderRadius: '8px', color: 'var(--color-foreground)', fontSize: '12px' }} />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4">Campaign Budget vs Spent</h3>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={campaignPerf}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="name" stroke="var(--color-muted-foreground)" style={{ fontSize: '10px' }} />
                <YAxis stroke="var(--color-muted-foreground)" style={{ fontSize: '11px' }} />
                <Tooltip contentStyle={{ backgroundColor: 'var(--color-popover)', border: '1px solid var(--color-border)', borderRadius: '8px', color: 'var(--color-foreground)', fontSize: '12px' }} />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Bar dataKey="budget" name="Budget" fill="var(--color-muted)" radius={[3, 3, 0, 0]} />
                <Bar dataKey="spent" name="Spent" fill="var(--color-primary)" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      {/* Reward ROI */}
      <div className="bg-card rounded-xl border border-border p-5">
        <h3 className="text-sm font-semibold text-foreground mb-4">Campaign Reward ROI (%)</h3>
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rewardROI}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="name" stroke="var(--color-muted-foreground)" style={{ fontSize: '10px' }} />
              <YAxis stroke="var(--color-muted-foreground)" style={{ fontSize: '11px' }} />
              <Tooltip contentStyle={{ backgroundColor: 'var(--color-popover)', border: '1px solid var(--color-border)', borderRadius: '8px', color: 'var(--color-foreground)', fontSize: '12px' }} formatter={(v) => [`${v}%`, 'ROI']} />
              <Bar dataKey="roi" name="ROI %" fill="var(--color-success)" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsReports;
